/*
  AMAVO CLINICAL — predictive search
  ------------------------------------------------------------------
  An addition on top of a working form. Without this file the drawer
  still opens (native <dialog>), the input still submits to /search, and
  the popular searches are still links.

  What it adds: results as you type, keyboard navigation through them,
  and a live region so a screen reader hears how many results appeared.
*/

(() => {
  'use strict';

  const ENDPOINT = (window.amavo && window.amavo.routes && window.amavo.routes.predictive_search_url) || '/search/suggest';
  const MIN_CHARS = 2;
  const DEBOUNCE = 250;

  class AmvSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-amv-search-input]');
      this.results = this.querySelector('[data-amv-search-results]');
      this.defaults = this.querySelector('[data-amv-search-default]');
      this.status = this.querySelector('[data-amv-search-status]');
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('keydown', (e) => this.onKey(e));

      // Focus the field when the drawer opens, but only after the
      // browser has finished its own focus handling for <dialog>.
      const dialog = this.querySelector('dialog');
      if (dialog) {
        const observer = new MutationObserver(() => {
          if (dialog.open) requestAnimationFrame(() => this.input.focus());
        });
        observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    schedule() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();

      if (term.length < MIN_CHARS) {
        this.clear();
        return;
      }
      this.timer = setTimeout(() => this.run(term), DEBOUNCE);
    }

    clear() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.defaults) this.defaults.hidden = false;
      if (this.status) this.status.textContent = '';
    }

    async run(term) {
      // A newer keystroke wins: abort the request in flight rather than
      // letting a slow one overwrite a fresher result.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url = `${ENDPOINT}?q=${encodeURIComponent(term)}` +
        '&resources[type]=product,article,page' +
        `&resources[limit]=${this.dataset.limit || 6}` +
        '&section_id=predictive-search';

      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.statusText);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('.amv-psearch');
        if (!fragment) return;

        this.results.innerHTML = '';
        this.results.appendChild(fragment);
        this.input.setAttribute('aria-expanded', 'true');
        if (this.defaults) this.defaults.hidden = true;

        const count = Number(fragment.dataset.count || 0);
        if (this.status) {
          this.status.textContent = count === 0
            ? (this.dataset.none || 'Niciun rezultat')
            : `${count}`;
        }
        this.options = Array.from(this.results.querySelectorAll('a'));
        this.active = -1;
      } catch (e) {
        if (e.name !== 'AbortError') this.clear();
      }
    }

    onKey(event) {
      if (!this.options || !this.options.length) return;
      const last = this.options.length - 1;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.active = event.key === 'ArrowDown'
          ? (this.active >= last ? 0 : this.active + 1)
          : (this.active <= 0 ? last : this.active - 1);
        this.options[this.active].focus();
        return;
      }

      if (event.key === 'Escape') {
        this.clear();
        this.input.value = '';
      }
    }
  }

  if (!customElements.get('amv-search')) customElements.define('amv-search', AmvSearch);
})();
